import { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import colors from 'styles/themes/colors';
import { EntityContext } from 'context/entityContext';

import {
  loadGeneralSettings,
  shouldLoadGeneralSettings,
} from 'redux/reducers/generalSettings';
import { loadCustomers } from 'redux/reducers/customers';

import getColorScale from '../helpers/getColorScale';
import { RootState } from 'redux/rootReducer';

export { useColorScale };

/**
 * This hook returns configured color scale for any PerformanceTrend graph.
 * It returns a color scale using the default data colors for the main trend lines,
 * or a color scale using either CasperGrey or customer colors for the complementary
 * trend lines.
 *
 * @params {Array} trends
 * @params {Boolean} isComplementary
 *
 * @returns {function}
 */
function useColorScale(trends: any[], isComplementary?: boolean): any {
  const { compareMode, dataColors, entityType } = useContext(EntityContext);

  // Is the current entity type 'release'?
  const isRelease = entityType === 'release';

  // Get the correct customer type
  const customerType = isRelease ? 'downloadCustomers' : 'streamingCustomers';

  // Get redux dispatch
  const dispatch = useDispatch();

  // Dispatch general settings and customers load actions
  useEffect(() => {
    if (dispatch(shouldLoadGeneralSettings())) {
      dispatch(loadGeneralSettings());
    }

    dispatch(loadCustomers());
  }, [dispatch]);

  // Get the customer colors from the general settings
  const customerColors = useSelector((state: RootState) => {
    if (state?.generalSettings.success) {
      return state.generalSettings.data.customerColors[customerType];
    }
  });

  // Get the fallback customer colors from the customers
  const fallbackCustomerColors = useSelector(
    (state: RootState) => state.customers.data
  );

  /**
   * Create a colorScale based on whether this is a complementary graph
   * or the main trend graph.
   */
  const colorScale = useCallback(
    isComplementary
      ? getColorScale(
          trends,
          compareMode ? dataColors : [colors.CasperGrey],
          customerColors,
          fallbackCustomerColors,
          compareMode
        )
      : getColorScale(
          trends,
          dataColors,
          customerColors,
          fallbackCustomerColors,
          compareMode
        ),
    [trends, customerColors, fallbackCustomerColors, compareMode]
  );

  return colorScale;
}
